import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  ShoppingCart,
  X,
  Search,
  RefreshCw,
  Target,
  Briefcase,
  AlertCircle,
  Loader,
  Lightbulb,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import WhatIfAnalyzer from './WhatIfAnalyzer';


const API_BASE = 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Sector mapping for common stocks
const SECTOR_MAP = {
  'AAPL': 'Technology', 'MSFT': 'Technology', 'GOOGL': 'Technology', 'AMZN': 'Technology',
  'META': 'Technology', 'NVDA': 'Technology', 'TSLA': 'Consumer Discretionary', 'AMD': 'Technology',
  'INTC': 'Technology', 'CRM': 'Technology', 'ORCL': 'Technology', 'ADBE': 'Technology',
  'JPM': 'Financials', 'BAC': 'Financials', 'WFC': 'Financials', 'GS': 'Financials',
  'MS': 'Financials', 'C': 'Financials', 'V': 'Financials', 'MA': 'Financials',
  'JNJ': 'Healthcare', 'UNH': 'Healthcare', 'PFE': 'Healthcare', 'MRK': 'Healthcare',
  'ABBV': 'Healthcare', 'LLY': 'Healthcare', 'TMO': 'Healthcare', 'ABT': 'Healthcare',
  'XOM': 'Energy', 'CVX': 'Energy', 'COP': 'Energy', 'SLB': 'Energy',
  'PG': 'Consumer Staples', 'KO': 'Consumer Staples', 'PEP': 'Consumer Staples', 'WMT': 'Consumer Staples',
  'HD': 'Consumer Discretionary', 'MCD': 'Consumer Discretionary', 'NKE': 'Consumer Discretionary',
  'DIS': 'Communication Services', 'NFLX': 'Communication Services', 'T': 'Communication Services',
  'VZ': 'Communication Services', 'CMCSA': 'Communication Services',
  'BA': 'Industrials', 'CAT': 'Industrials', 'GE': 'Industrials', 'UPS': 'Industrials',
  'NEE': 'Utilities', 'DUK': 'Utilities', 'SO': 'Utilities',
  'AMT': 'Real Estate', 'PLD': 'Real Estate', 'SPG': 'Real Estate',
  'LIN': 'Materials', 'APD': 'Materials', 'ECL': 'Materials',
};

const SECTOR_COLORS = {
  'Technology': '#3b82f6',
  'Financials': '#22c55e',
  'Healthcare': '#ef4444',
  'Energy': '#f59e0b',
  'Consumer Staples': '#8b5cf6',
  'Consumer Discretionary': '#ec4899',
  'Communication Services': '#06b6d4',
  'Industrials': '#64748b',
  'Utilities': '#84cc16',
  'Real Estate': '#f97316',
  'Materials': '#a855f7',
  'Other': '#6b7280',
};

// Helper functions
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const PaperTrading = ({ realPortfolio, user }) => {
  // Paper Trading State
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [riskData, setRiskData] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState(null);

  const fetchRiskAnalysis = async () => {
    setRiskLoading(true);
    setRiskError(null);
    try {
      const headers = { 'Content-Type': 'application/json', 'x-user-id': userId };
      const liveValue = account?.portfolioValue || account?.totalEquity || null;
      const url = liveValue
        ? `${API_BASE}/paper-trading/risk-analysis?liveValue=${liveValue}`
        : `${API_BASE}/paper-trading/risk-analysis`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load risk analysis');
      setRiskData(data);
    } catch (e) {
      setRiskError(e.message);
    }
    setRiskLoading(false);
  };
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [livePrice, setLivePrice] = useState(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [orderForm, setOrderForm] = useState({
    symbol: '',
    side: 'buy',
    type: 'market',
    qty: '',
    notional: '',
    limitPrice: '',
    stopPrice: '',
    useNotional: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // What-If Analyzer State
  const [showAnalyzer, setShowAnalyzer] = useState(false);

  // Get user from localStorage - your app uses 'investorq_user'
  const storedUser = localStorage.getItem('investorq_user');
  
  let userId = null;
  let userEmail = null;
  let userName = null;
  
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      userId = parsedUser.id;
      userEmail = parsedUser.email;
      userName = parsedUser.name;
    } catch (e) {
      console.error('Error parsing investorq_user:', e);
    }
  }
  // Fallback to user prop (Google OAuth users don't use localStorage)
  if (!userId && user?.id) userId = user.id;
  if (!userEmail && user?.email) userEmail = user.email;
  if (!userName && user?.name) userName = user.name;

  // Fetch Paper Trading Data
  const fetchData = useCallback(async () => {
    if (!userId) return; // Don't fetch if no user
    
    setLoading(true);
    try {
      const headers = { 'x-user-id': String(userId) };

      const accountRes = await fetch(`${API_BASE}/paper-trading/account`, { headers });
      if (accountRes.ok) {
        const accountData = await accountRes.json();
        setAccount(accountData);
      }

      const positionsRes = await fetch(`${API_BASE}/paper-trading/positions`, { headers });
      if (positionsRes.ok) {
        const positionsData = await positionsRes.json();
        setPositions(positionsData.positions || []);
      }

      const ordersRes = await fetch(`${API_BASE}/paper-trading/orders?status=all&limit=50`, { headers });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData.orders || []);
      }

      const historyRes = await fetch(`${API_BASE}/paper-trading/account/history?period=1M&timeframe=1D`, { headers });
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setPortfolioHistory(historyData.chartData || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load paper trading data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Clear data and refetch when user changes
  useEffect(() => {
    // Reset all state when userId changes
    setAccount(null);
    setPositions([]);
    setOrders([]);
    setPortfolioHistory([]);
    
    if (userId) {
      fetchData();
    }
  }, [userId, fetchData]);

  // Search for stocks
  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/paper-trading/assets/search?q=${encodeURIComponent(query)}`, {
        headers: { 'x-user-id': String(userId) },
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.assets || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const selectStock = async (asset) => {
    setOrderForm({ ...orderForm, symbol: asset.symbol });
    setSearchResults([]);
    setSearchQuery(asset.symbol);
    setLivePrice(null);
    setLivePriceLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json', 'x-user-id': userId };
      const res = await fetch(`${API_BASE}/paper-trading/quote/${asset.symbol}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLivePrice(data.price || data.latestPrice || data.askPrice || null);
      }
    } catch (e) { console.error('Quote fetch error:', e); }
    setLivePriceLoading(false);
  };

  // Submit order
  const submitOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const orderData = {
        symbol: orderForm.symbol,
        side: orderForm.side,
        type: orderForm.type,
      };

      if (orderForm.useNotional) {
        orderData.notional = parseFloat(orderForm.notional);
      } else {
        orderData.qty = parseFloat(orderForm.qty);
      }

      if (orderForm.type === 'limit' || orderForm.type === 'stop_limit') {
        orderData.limitPrice = parseFloat(orderForm.limitPrice);
      }
      if (orderForm.type === 'stop' || orderForm.type === 'stop_limit') {
        orderData.stopPrice = parseFloat(orderForm.stopPrice);
      }

      const res = await fetch(`${API_BASE}/paper-trading/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': String(userId),
        },
        body: JSON.stringify(orderData),
      });

      const data = await res.json();
      if (res.ok) {
        setShowOrderModal(false);
        setOrderForm({
          symbol: '',
          side: 'buy',
          type: 'market',
          qty: '',
          notional: '',
          limitPrice: '',
          stopPrice: '',
          useNotional: false,
        });
        setSearchQuery('');
        fetchData();
        alert(`✅ Simulated order executed!\n\n${orderForm.side.toUpperCase()} ${orderForm.qty || '$' + orderForm.notional} ${orderForm.symbol}\n\nRemember: This is virtual money for practice only.`);
      } else {
        alert(data.error || 'Failed to submit order');
      }
    } catch (err) {
      console.error('Order error:', err);
      alert('Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const closePosition = async (symbol) => {
    if (!window.confirm(`Close entire position in ${symbol}?`)) return;

    try {
      const res = await fetch(`${API_BASE}/paper-trading/positions/${symbol}`, {
        method: 'DELETE',
        headers: { 'x-user-id': String(userId) },
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Close position error:', err);
    }
  };


  // If no user logged in, show login required message
  if (!userId) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <AlertCircle size={64} color="#f59e0b" style={{ marginBottom: '1.5rem' }} />
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>Login Required</h2>
        <p style={{ color: '#94a3b8', marginBottom: '1.5rem', maxWidth: '400px' }}>
          Please log in to access Paper Trading. Each user gets their own $100,000 virtual account to practice trading.
        </p>
        <button
          onClick={() => window.location.href = '/login'}
          style={{
            padding: '0.75rem 2rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem'
          }}
        >
          Log In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <Loader size={48} color="#8b5cf6" className="spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Educational Disclaimer Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <AlertCircle size={20} color="#fbbf24" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <div style={{ fontWeight: '600', color: '#fbbf24', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            📚 Educational Simulator
            <span style={{
              background: 'rgba(251, 191, 36, 0.2)',
              padding: '0.125rem 0.5rem',
              borderRadius: '1rem',
              fontSize: '0.625rem',
              fontWeight: '700',
              textTransform: 'uppercase'
            }}>Virtual Money</span>
          </div>
          <p style={{ color: '#fcd34d', fontSize: '0.8rem', margin: 0, lineHeight: '1.4' }}>
            This is a paper trading simulator for learning purposes only. All trades use virtual money ($100K) and do not involve real securities. 
            Not investment advice – consult a financial advisor for real decisions.
          </p>
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
            <Target size={32} color="#8b5cf6" />
            Paper Trading
            <span style={{
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              fontSize: '0.625rem',
              fontWeight: '700',
              color: '#000',
              textTransform: 'uppercase',
              marginLeft: '0.5rem'
            }}>Simulator</span>
          </h1>
          <p style={{ color: '#94a3b8', marginTop: '0.25rem', margin: 0 }}>
            Practice trading with $100,000 virtual money • Learn risk-free
            {userEmail && (
              <span style={{ 
                marginLeft: '0.75rem', 
                padding: '0.125rem 0.5rem', 
                background: 'rgba(139, 92, 246, 0.2)', 
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                color: '#c4b5fd'
              }}>
                👤 {userEmail}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowAnalyzer(!showAnalyzer)}
            style={{
              padding: '0.625rem 1rem',
              background: showAnalyzer ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#475569',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            <Lightbulb size={16} />
            What-If Analyzer
          </button>
          <button
            onClick={fetchData}
            style={{
              padding: '0.625rem 1rem',
              background: '#475569',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowOrderModal(true)}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <ShoppingCart size={16} />
            New Trade
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertCircle size={20} color="#f87171" />
          <span style={{ color: '#fca5a5' }}>{error}</span>
        </div>
      )}

      {/* What-If Portfolio Analyzer */}
      {showAnalyzer && (
        <WhatIfAnalyzer 
          realPortfolio={realPortfolio}
          onExecuteInPaperTrading={async (trades) => {
            if (!userId) {
              alert('❌ Please log in to execute trades in Paper Trading.');
              return;
            }
            let successCount = 0;
            let failCount = 0;
            for (const trade of trades) {
              try {
                const res = await fetch(`${API_BASE}/paper-trading/orders`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': String(userId),
                  },
                  body: JSON.stringify({
                    symbol: trade.symbol,
                    side: trade.action,
                    notional: trade.amount,
                    type: 'market',
                  }),
                });
                const data = await res.json();
                if (res.ok) {
                  successCount++;
                } else {
                  failCount++;
                  console.error(`Trade failed for ${trade.symbol}:`, data.error);
                }
              } catch (err) {
                failCount++;
                console.error('Error executing trade:', err);
              }
            }
            fetchData();
            if (failCount === 0) {
              alert(`✅ ${successCount} trade${successCount > 1 ? 's' : ''} executed in Paper Trading!`);
            } else if (successCount === 0) {
              alert(`❌ All trades failed. Check your session and try again.`);
            } else {
              alert(`⚠️ ${successCount} succeeded, ${failCount} failed. Check console for details.`);
            }
          }}
        />
      )}

      {/* Account Summary Cards */}
      {account && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Briefcase size={18} color="#8b5cf6" />
              <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Portfolio Value</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(account.portfolioValue)}</div>
          </div>

          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <DollarSign size={18} color="#22c55e" />
              <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Cash Available</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(account.cash)}</div>
          </div>

          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Activity size={18} color="#3b82f6" />
              <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Buying Power</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(account.buyingPower)}</div>
          </div>

          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {account.totalEquity >= 100000 ? <TrendingUp size={18} color="#22c55e" /> : <TrendingDown size={18} color="#ef4444" />}
              <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Total P&L</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: account.totalEquity >= 100000 ? '#34d399' : '#f87171' }}>
              {formatCurrency(account.totalEquity - 100000)}
            </div>
            <div style={{ fontSize: '0.875rem', color: account.totalEquity >= 100000 ? '#34d399' : '#f87171' }}>
              {formatPercent(((account.totalEquity - 100000) / 100000) * 100)}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Chart */}
      {portfolioHistory.length > 0 && (
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', margin: '0 0 1rem 0' }}>Portfolio Performance</h2>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={portfolioHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" tickFormatter={(val) => new Date(val).toLocaleDateString()} stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`} fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '0.5rem' }}
                formatter={(val) => [formatCurrency(val), 'Value']}
                labelFormatter={(val) => new Date(val).toLocaleDateString()}
              />
              <Area type="monotone" dataKey="equity" stroke="#8b5cf6" fill="url(#colorEquity)" strokeWidth={2} />
              <defs>
                <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #334155', paddingBottom: '0.5rem' }}>
        {['portfolio', 'orders', 'history', 'risk'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.5rem 1rem',
              background: activeTab === tab ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
              border: activeTab === tab ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid transparent',
              borderRadius: '0.5rem',
              color: activeTab === tab ? '#c4b5fd' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              textTransform: 'capitalize'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Symbol</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Shares</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }} title="Your average purchase price per share">Avg Cost</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }} title="Current market price per share">Current</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }} title="Total market value (Shares × Current Price)">Value</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem', cursor: 'help' }} title="Total Profit/Loss: (Current Price - Avg Cost) × Shares. Green = profit, Red = loss">
                  P&L <span style={{ fontSize: '0.6rem', color: '#64748b' }}>ⓘ</span>
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem', cursor: 'help' }} title="Today's change in value based on price movement since market open">
                  Today <span style={{ fontSize: '0.6rem', color: '#64748b' }}>ⓘ</span>
                </th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                    No positions yet. Click "New Trade" to get started!
                  </td>
                </tr>
              ) : (
                positions.map((pos) => (
                  <tr key={pos.symbol} style={{ borderTop: '1px solid #334155' }}>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{pos.symbol}</td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>{pos.qty?.toFixed(2)}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#cbd5e1' }}>{formatCurrency(pos.avgEntryPrice)}</td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>{formatCurrency(pos.currentPrice)}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(pos.marketValue)}</td>
                    <td style={{ padding: '1rem', textAlign: 'right' }} title={`Unrealized P&L: ${formatCurrency(pos.unrealizedPL)} (${formatPercent(pos.unrealizedPLPct)})`}>
                      <div style={{ color: pos.unrealizedPL >= 0 ? '#34d399' : '#f87171' }}>{formatCurrency(pos.unrealizedPL)}</div>
                      <div style={{ fontSize: '0.75rem', color: pos.unrealizedPL >= 0 ? '#34d399' : '#f87171' }}>{formatPercent(pos.unrealizedPLPct)}</div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: (pos.todayPL || 0) >= 0 ? '#34d399' : '#f87171' }} title="Change in position value since market open today">
                      {formatCurrency(pos.todayPL || 0)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={() => closePosition(pos.symbol)}
                        style={{
                          padding: '0.375rem 0.75rem',
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '0.375rem',
                          color: '#fca5a5',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        Close
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Symbol</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Side</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Qty</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Type</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.filter(o => ['new', 'accepted', 'pending_new', 'partially_filled', 'pending'].includes(o.status)).length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No open orders</td>
                </tr>
              ) : (
                orders.filter(o => ['new', 'accepted', 'pending_new', 'partially_filled', 'pending'].includes(o.status)).map((order) => (
                  <tr key={order.id} style={{ borderTop: '1px solid #334155' }}>
                    <td style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.875rem' }}>{new Date(order.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{order.symbol}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: order.side === 'buy' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: order.side === 'buy' ? '#34d399' : '#f87171'
                      }}>
                        {order.side?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>{order.qty}</td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: '#cbd5e1' }}>{order.type}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ padding: '0.25rem 0.5rem', background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', borderRadius: '0.25rem', fontSize: '0.75rem' }}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Risk Tab */}
      {activeTab === 'risk' && (
        <div>
          {!riskData && !riskLoading && !riskError && (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📊</div>
              <h3 style={{ color: '#e2e8f0', marginBottom: '0.5rem' }}>Portfolio Risk Analysis</h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem', maxWidth: 400, margin: '0 auto 1.5rem' }}>
                Run a full risk analysis on your paper trading portfolio — Sharpe ratio, Monte Carlo simulation, concentration metrics, rebalancing suggestions, and more.
              </p>
              <button onClick={fetchRiskAnalysis} style={{
                padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                border: 'none', borderRadius: '0.5rem', color: 'white',
                fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer'
              }}>
                Run Analysis
              </button>
            </div>
          )}

          {riskLoading && (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⚙️</div>
              <p style={{ color: '#94a3b8' }}>Running analysis... this takes 15–30 seconds</p>
              <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.5rem' }}>Fetching 1 year of price history per position</p>
            </div>
          )}

          {riskError && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.75rem', padding: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: '#f87171', marginBottom: '1rem' }}>{riskError}</p>
              <button onClick={fetchRiskAnalysis} style={{
                padding: '0.5rem 1.5rem', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '0.5rem', color: '#f87171', cursor: 'pointer', fontSize: '0.875rem'
              }}>Retry</button>
            </div>
          )}

          {riskData && !riskLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

              {/* Refresh button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={fetchRiskAnalysis} style={{
                  padding: '0.4rem 1rem', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: '0.5rem', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.8rem'
                }}>↻ Refresh</button>
              </div>

              {/* Key Metrics Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                {[
                  { label: 'Sharpe Ratio', value: riskData.current?.sharpeRatio?.toFixed(2), sub: '>1 is good', color: riskData.current?.sharpeRatio > 1 ? '#22c55e' : riskData.current?.sharpeRatio > 0 ? '#f59e0b' : '#ef4444' },
                  { label: 'Expected Return', value: `${riskData.current?.portfolioReturn?.toFixed(1)}%`, sub: 'Annualized', color: riskData.current?.portfolioReturn > 0 ? '#22c55e' : '#ef4444' },
                  { label: 'Volatility', value: `${riskData.current?.portfolioRisk?.toFixed(1)}%`, sub: 'Annual std dev', color: '#f59e0b' },
                  { label: 'Portfolio Value', value: `$${riskData.current?.totalValue?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, sub: 'Paper account', color: '#60a5fa' },
                  { label: 'Effective Holdings', value: riskData.diversification?.effectiveHoldings?.toFixed(1), sub: 'Diversification', color: '#a78bfa' },
                  { label: 'Top 5 Concentration', value: `${riskData.diversification?.top5Concentration?.toFixed(1)}%`, sub: '<60% is healthy', color: riskData.diversification?.top5Concentration < 60 ? '#22c55e' : '#ef4444' },
                ].map(m => (
                  <div key={m.label} style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem' }}>
                    <div style={{ color: '#64748b', fontSize: '0.72rem', marginBottom: '0.25rem' }}>{m.label}</div>
                    <div style={{ color: m.color, fontSize: '1.4rem', fontWeight: '700', lineHeight: 1 }}>{m.value}</div>
                    <div style={{ color: '#475569', fontSize: '0.68rem', marginTop: '0.25rem' }}>{m.sub}</div>
                  </div>
                ))}
              </div>

              {/* Monte Carlo */}
              <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                <h3 style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600', marginBottom: '1rem' }}>
                  🎲 Monte Carlo Simulation — 10 Year Projection (1,000 runs)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
                  {[
                    { label: 'Best Case (P90)', value: riskData.monteCarlo?.results?.percentile90, color: '#22c55e' },
                    { label: 'Likely High (P75)', value: riskData.monteCarlo?.results?.percentile75, color: '#86efac' },
                    { label: 'Median', value: riskData.monteCarlo?.results?.median, color: '#60a5fa' },
                    { label: 'Likely Low (P25)', value: riskData.monteCarlo?.results?.percentile25, color: '#fbbf24' },
                    { label: 'Worst Case (P10)', value: riskData.monteCarlo?.results?.percentile10, color: '#f87171' },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(15,23,42,0.5)', borderRadius: '0.5rem' }}>
                      <div style={{ color: '#64748b', fontSize: '0.68rem', marginBottom: '0.25rem' }}>{s.label}</div>
                      <div style={{ color: s.color, fontSize: '1rem', fontWeight: '700' }}>
                        ${s.value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ color: '#475569', fontSize: '0.7rem', marginTop: '0.75rem' }}>
                  Starting value: ${riskData.monteCarlo?.initialValue?.toLocaleString('en-US', { maximumFractionDigits: 0 })} · Based on 1yr historical returns blended with 10% long-run prior
                </div>
              </div>

              {/* Rebalancing */}
              {riskData.rebalancing?.needed && (
                <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                  <h3 style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600', marginBottom: '1rem' }}>
                    ⚖️ Rebalancing Suggestions
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {riskData.rebalancing.trades.map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', background: 'rgba(15,23,42,0.4)', borderRadius: '0.5rem' }}>
                        <span style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '0.875rem' }}>{t.symbol}</span>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{t.currentWeight}% → {t.targetWeight}%</span>
                          <span style={{
                            padding: '0.2rem 0.6rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '700',
                            background: t.action === 'BUY' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
                            color: t.action === 'BUY' ? '#22c55e' : '#ef4444'
                          }}>{t.action} {Math.abs(t.difference).toFixed(1)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tax Loss Harvesting */}
              {riskData.taxLossHarvesting?.opportunities > 0 && (
                <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                  <h3 style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    🌿 Tax-Loss Harvesting Opportunities
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1rem' }}>
                    Potential savings: <span style={{ color: '#22c55e', fontWeight: '700' }}>${riskData.taxLossHarvesting.totalPotentialSavings?.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                  </p>
                  {riskData.taxLossHarvesting.details.slice(0, 5).map((opp, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderTop: i > 0 ? '1px solid #1e293b' : 'none' }}>
                      <span style={{ fontWeight: '600', color: '#e2e8f0' }}>{opp.symbol}</span>
                      <span style={{ color: '#f87171', fontSize: '0.875rem' }}>-${opp.unrealizedLoss?.toLocaleString('en-US', { maximumFractionDigits: 0 })} loss</span>
                      <span style={{ color: '#22c55e', fontSize: '0.875rem' }}>Save ${opp.taxSavings?.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Correlation Matrix */}
              {riskData.correlationMatrix && riskData.correlationMatrix.length > 1 && (
                <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem', overflowX: 'auto' }}>
                  <h3 style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600', marginBottom: '1rem' }}>
                    🔗 Correlation Matrix
                  </h3>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.4rem 0.6rem', color: '#64748b', width: '60px' }}></th>
                        {riskData.correlationMatrix.map(r => (
                          <th key={r.symbol} style={{ padding: '0.4rem 0.6rem', color: '#94a3b8', fontWeight: '600', textAlign: 'center' }}>{r.symbol}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {riskData.correlationMatrix.map((row, i) => (
                        <tr key={row.symbol}>
                          <td style={{ padding: '0.4rem 0.6rem', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>{row.symbol}</td>
                          {row.correlations.map((c, j) => (
                            <td key={j} style={{
                              padding: '0.4rem 0.6rem', textAlign: 'center', borderRadius: '0.25rem',
                              background: i === j ? 'rgba(99,102,241,0.2)' : c > 0.7 ? 'rgba(239,68,68,0.15)' : c > 0.4 ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.1)',
                              color: i === j ? '#a5b4fc' : c > 0.7 ? '#f87171' : c > 0.4 ? '#fbbf24' : '#86efac'
                            }}>{c.toFixed(2)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ color: '#475569', fontSize: '0.68rem', marginTop: '0.75rem' }}>
                    🟢 Low correlation (&lt;0.4) · 🟡 Medium (0.4–0.7) · 🔴 High (&gt;0.7) — lower is better for diversification
                  </p>
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Symbol</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Side</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Fill Price</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.875rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.filter(o => ['filled', 'canceled', 'cancelled', 'expired', 'rejected'].includes(o.status)).length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No order history yet</td>
                </tr>
              ) : (
                orders.filter(o => ['filled', 'canceled', 'cancelled', 'expired', 'rejected'].includes(o.status)).slice(0, 50).map((order) => (
                  <tr key={order.id} style={{ borderTop: '1px solid #334155' }}>
                    <td style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.875rem' }}>{new Date(order.filledAt || order.updatedAt || order.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{order.symbol}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: order.side === 'buy' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: order.side === 'buy' ? '#34d399' : '#f87171'
                      }}>
                        {order.side?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>{(order.filledQty || order.qty)?.toFixed(2)}</td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>{order.filledAvgPrice ? formatCurrency(order.filledAvgPrice) : '-'}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        background: order.status === 'filled' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                        color: order.status === 'filled' ? '#34d399' : '#9ca3af'
                      }}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Modal */}
      {showOrderModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '1rem',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '400px',
            border: '1px solid #334155',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>New Trade</h2>
              <button
                onClick={() => { setShowOrderModal(false); setLivePrice(null); }}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0.25rem' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={submitOrder}>
              {/* Stock Search */}
              <div style={{ marginBottom: '1rem', position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>Symbol</label>
                <div style={{ position: 'relative' }}>
                  <Search size={16} color="#64748b" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search stocks..."
                    style={{
                      width: '100%',
                      padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                {searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '0.5rem',
                    marginTop: '0.25rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 10
                  }}>
                    {searchResults.map((asset) => (
                      <button
                        key={asset.symbol}
                        type="button"
                        onClick={() => selectStock(asset)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid #334155',
                          color: 'white',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          gap: '0.5rem'
                        }}
                      >
                        <span style={{ fontWeight: '600' }}>{asset.symbol}</span>
                        <span style={{ color: '#64748b', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Buy/Sell Toggle */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>Action</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setOrderForm({ ...orderForm, side: 'buy' })}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: orderForm.side === 'buy' ? '#22c55e' : '#334155',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Buy
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderForm({ ...orderForm, side: 'sell' })}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: orderForm.side === 'sell' ? '#ef4444' : '#334155',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Sell
                  </button>
                </div>
              </div>

              {/* Live Price Display */}
              {orderForm.symbol && (
                <div style={{ marginBottom: '1rem', padding: '0.6rem 0.875rem', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Current Price</span>
                  <span style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '1rem' }}>
                    {livePriceLoading ? '...' : livePrice ? `$${livePrice.toFixed(2)}` : '—'}
                  </span>
                </div>
              )}

              {/* Order Type */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>Order Type</label>
                <select
                  value={orderForm.type}
                  onChange={(e) => setOrderForm({ ...orderForm, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="market">Market</option>
                  <option value="limit">Limit</option>
                  <option value="stop">Stop</option>
                  <option value="stop_limit">Stop Limit</option>
                </select>
              </div>

              {/* Quantity */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>
                  {orderForm.useNotional ? 'Dollar Amount' : 'Shares'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="any"
                    value={orderForm.useNotional ? orderForm.notional : orderForm.qty}
                    onChange={(e) => setOrderForm({
                      ...orderForm,
                      [orderForm.useNotional ? 'notional' : 'qty']: e.target.value,
                    })}
                    placeholder={orderForm.useNotional ? 'Amount in $' : 'Number of shares'}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '0.875rem'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setOrderForm({ ...orderForm, useNotional: !orderForm.useNotional })}
                    style={{
                      padding: '0.75rem',
                      background: '#334155',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    {orderForm.useNotional ? 'Shares' : '$'}
                  </button>
                </div>
              </div>

              {/* Total Cost + Affordability Check */}
              {livePrice && (orderForm.qty || orderForm.notional) && (() => {
                const qty = orderForm.useNotional
                  ? parseFloat(orderForm.notional) / livePrice
                  : parseFloat(orderForm.qty);
                const totalCost = orderForm.useNotional
                  ? parseFloat(orderForm.notional)
                  : qty * livePrice;
                const cash = account?.cash || 0;
                const canAfford = orderForm.side === 'sell' || totalCost <= cash;
                const validQty = qty > 0 && !isNaN(qty);

                if (!validQty) return null;
                return (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      padding: '0.75rem', borderRadius: '0.5rem',
                      background: canAfford ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                      border: `1px solid ${canAfford ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                      display: 'flex', flexDirection: 'column', gap: '0.4rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Shares</span>
                        <span style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '0.8rem' }}>{qty.toFixed(4)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Est. Total Cost</span>
                        <span style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '0.9rem' }}>${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Cash Available</span>
                        <span style={{ color: canAfford ? '#22c55e' : '#ef4444', fontWeight: '600', fontSize: '0.8rem' }}>${cash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      {!canAfford && (
                        <div style={{ marginTop: '0.25rem', color: '#f87171', fontSize: '0.75rem', fontWeight: '600' }}>
                          ⚠️ Insufficient funds — need ${(totalCost - cash).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Limit Price */}
              {(orderForm.type === 'limit' || orderForm.type === 'stop_limit') && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>Limit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={orderForm.limitPrice}
                    onChange={(e) => setOrderForm({ ...orderForm, limitPrice: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Stop Price */}
              {(orderForm.type === 'stop' || orderForm.type === 'stop_limit') && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.375rem' }}>Stop Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={orderForm.stopPrice}
                    onChange={(e) => setOrderForm({ ...orderForm, stopPrice: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Simulator Notice */}
              <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.2)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                <p style={{ color: '#fcd34d', fontSize: '0.7rem', margin: 0 }}>
                  🎮 <strong>Simulated Trade</strong> • Uses virtual money only
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !orderForm.symbol || (!orderForm.qty && !orderForm.notional)}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: orderForm.side === 'buy' 
                    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: submitting || !orderForm.symbol || (!orderForm.qty && !orderForm.notional) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  opacity: submitting || !orderForm.symbol || (!orderForm.qty && !orderForm.notional) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {submitting ? (
                  <Loader size={20} className="spin" />
                ) : (
                  `${orderForm.side === 'buy' ? 'Buy' : 'Sell'} ${orderForm.symbol || 'Stock'} (Simulated)`
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer Disclaimer */}
      <div style={{
        marginTop: '2rem',
        padding: '1.25rem',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📚</span>
          <span style={{ fontWeight: '600', color: '#94a3b8' }}>Educational Purpose Disclaimer</span>
        </div>
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          margin: 0,
          lineHeight: '1.6'
        }}>
          <strong style={{ color: '#94a3b8' }}>This is a paper trading simulator for educational and practice purposes only.</strong> All trades 
          are executed with virtual money and do not involve real securities, actual brokerage accounts, or real market 
          transactions. The $100,000 balance is simulated currency with no real-world value. Stock prices shown are 
          real market data but all trading activity is purely hypothetical.
        </p>
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          margin: '0.75rem 0 0 0',
          lineHeight: '1.6'
        }}>
          <strong style={{ color: '#94a3b8' }}>Not Investment Advice:</strong> Simulated results do not guarantee future performance. 
          Paper trading does not account for real market conditions like slippage, liquidity, or emotional factors. 
          Always consult with a qualified financial advisor before making actual investment decisions.
        </p>
      </div>

      {/* CSS for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PaperTrading;